import React from 'react';

import { useIntl } from 'react-intl';

import {
	mediaInsertMessages,
	toolbarInsertBlockMessages as messages,
} from '@atlaskit/editor-common/messages';
import AiSparkleIcon from '@atlaskit/icon/core/ai-sparkle';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';

import type { AIImageGenerationPlugin } from './aiImageGenerationPluginType';
import { showAIImageGenerationPopup } from './pm-plugins/actions';
import { createImageGenPlugin } from './pm-plugins/main';
import { pluginKey } from './pm-plugins/plugin-key';
import { AIImageGenerationPopup } from './ui/AIImageGenerationPopup';
import { getAIImageGenerationQuickInsertComponents } from './ui/quick-insert/getAIImageGenerationQuickInsertComponents';
import { MediaInsertImageGenerationTab } from './ui/MediaInsertImageGenerationTab';

/**
 * Renders the "Create" tab label using the same
 * `intl.formatMessage(...)` pattern that `MediaInsertPicker` uses for its
 * built-in `fileTabTitle` and `linkTabTitle` tabs. Defined at module scope so
 * the component reference is stable across renders.
 */
const GenerateTabLabel = (): React.JSX.Element => {
	const intl = useIntl();
	return <>{intl.formatMessage(mediaInsertMessages.generateTabTitle)}</>;
};

export const aiImageGenerationPlugin: AIImageGenerationPlugin = ({ api, config }) => {
	// Register a "Create" tab on the media insert picker. This keeps
	// the public `editor-plugin-media-insert` package free of `@atlassian/*`
	// dependencies — it only knows about the registry interface, not about
	// AI image generation.
	const { isRemixHidden, isRovoLLMEnabled } = config ?? {
		isRemixHidden: true,
		isRovoLLMEnabled: false,
	};

	const isRemixEnabled =
		isRovoLLMEnabled && expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true);
	const isRegisteredSlashCommandEnabled = isExperimentEnabled('platform_editor_slash_command');

	if (isRegisteredSlashCommandEnabled) {
		api?.uiControlRegistry?.actions.register(
			getAIImageGenerationQuickInsertComponents({
				api,
				isImageGenerationEnabled:
					expValEquals('cc_page_experiences_editor_image_generation', 'isEnabled', true) &&
					fg('m0_editor_image_generation_exclusion'),
			}),
		);
	}

	if (
		expValEquals('cc_page_experiences_editor_image_generation', 'isEnabled', true) &&
		api &&
		!isRemixHidden &&
		isRemixEnabled
	) {
		api?.mediaInsert?.actions.registerInsertTab({
			key: 'ai-image-generation',
			label: <GenerateTabLabel />,
			component: (props) => (
				<MediaInsertImageGenerationTab
					closeMediaInsertPicker={props.closeMediaInsertPicker}
					dispatchAnalyticsEvent={props.dispatchAnalyticsEvent}
					insertMediaSingle={props.insertMediaSingle}
					mediaProvider={props.mediaProvider}
					api={api}
				/>
			),
		});
	}

	return {
		name: 'aiImageGeneration',

		pmPlugins() {
			return [
				{
					name: 'aiImageGeneration',
					plugin: () => createImageGenPlugin(),
				},
			];
		},

		getSharedState(editorState) {
			if (!editorState) {
				return { isOpen: false };
			}
			const state = pluginKey.getState(editorState);
			if (!state) {
				return { isOpen: false };
			}
			return { isOpen: state.isOpen };
		},

		commands: {
			showAIImageGenerationPopup:
				() =>
				({ tr }) =>
					showAIImageGenerationPopup(tr),
		},

		contentComponent: ({
			editorView,
			popupsMountPoint,
			popupsBoundariesElement,
			popupsScrollableElement,
		}) => {
			if (!editorView) {
				return null;
			}

			return (
				<AIImageGenerationPopup
					api={api}
					editorView={editorView}
					popupsMountPoint={popupsMountPoint}
					popupsBoundariesElement={popupsBoundariesElement}
					popupsScrollableElement={popupsScrollableElement}
				/>
			);
		},

		pluginsOptions: isRegisteredSlashCommandEnabled
			? {}
			: {
					quickInsert: ({ formatMessage }) => {
						/**
						 * expValEquals('cc_page_experiences_editor_image_generation', 'isEnabled', true) is for overall experiment
						 *
						 * fg('m0_editor_image_generation_exclusion') for features that's been hidden in M0. If this is not on
						 * it should not show up in the quick insert.
						 */
						if (!expValEquals('cc_page_experiences_editor_image_generation', 'isEnabled', true)) {
							return [];
						}

						if (!fg('m0_editor_image_generation_exclusion')) {
							return [];
						}

						const title = formatMessage(messages.aiImageGeneration);

						return [
							{
								id: 'aiImageGeneration',
								title,
								description: formatMessage(messages.aiImageGenerationDescription),
								priority: 500,
								keywords: ['ai', 'generate', 'image', 'create', 'picture'],
								icon: () => <AiSparkleIcon label={title} color="currentColor" />,
								isDisabledOffline: true,
								action(insert) {
									const tr = insert('');
									api?.aiImageGeneration.commands.showAIImageGenerationPopup()({ tr });
									return tr;
								},
							},
						];
					},
				},
	};
};
